import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { interval, Observable, Subscription } from 'rxjs';
import { MessagesQuery } from '../message/messages.query';
import { MessagesService } from '../message/messages.service';
import { DiscussionService } from './discussion.service';
import { Snackbar } from '../../common/snackbar/snackbar';
import { SnackbarData } from '../../common/snackbar/snackbar-data';
import { ApiError } from '../../common/error/api-error';
import { ConfirmDialogComponent } from '../../common/components/confirm-dialog/confirm-dialog';
import { TextMessage } from '../message/text-message.model';
import { MessageInterface } from '../message/message-interface.model';
import { debounceTime } from 'rxjs/operators';
import { DiscussionModel } from '../../common/models/discussion-model';
import { Router } from '@angular/router';
import { ConfigProvider} from "../../config-provider.service";
import { ShareService } from '../share/share.service';
import { FilesVideoPlayerDialogComponent } from '../../files/components/video-player-dialog/video-player-dialog';
import { FilesImageViewerDialogComponent } from '../../files/components/image-viewer-dialog/image-viewer-dialog';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CurrentUser } from "../../common/providers/current-user";
import { User } from "../../common/models/user";
import { Organization } from "../../common/models/organization";
import { Athlete } from "../../common/models/athlete";
import { DownloaderHelper } from "../../document/helpers/downloader.helper";
import { TranslateModule } from "@ngx-translate/core";
import { AsyncPipe, LowerCasePipe, NgForOf, NgIf, NgOptimizedImage } from "@angular/common";
import { TruncatePipe } from "../../common/pipes/truncate.pipe";
import { DateClosestPipe } from "../../common/pipes/date-closest";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { InfiniteScrollComponent } from "../infinite-scroll/Infinite-scroll.component";
import { MessageComponent } from "../message/message.component";
import { MatInput } from "@angular/material/input";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";

@Component({
  selector: 'app-chat-discussion',
  templateUrl: './discussion.component.html',
  styleUrls: ['./discussion.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    AsyncPipe,
    LowerCasePipe,
    TruncatePipe,
    DateClosestPipe,
    FlexLayoutModule,
    MatCardModule,
    MatMenuModule,
    MatFormFieldModule,
    MatIcon,
    MatProgressSpinner,
    NgIf,
    MatIconButton,
    MatTooltip,
    InfiniteScrollComponent,
    NgForOf,
    MessageComponent,
    MatInput,
    CdkTextareaAutosize,
    NgOptimizedImage
  ],
  providers: [ShareService]
})
export class DiscussionComponent implements OnInit, OnDestroy {
    public user: User = {} as User;
    public discussion: DiscussionModel = {} as DiscussionModel;
    public viewRef: ComponentRef<any> | undefined ;
    public messages$: Observable<MessageInterface[]> | undefined;
    public isLoading$: Observable<boolean> = new Observable<boolean>();
    public chatMessageScrollHeight = 0;
    public files: Set<File> = new Set();
    public progress:any;
    public hasBeenScrolledOnce = false;
    public mobileDisplay = false;
    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;

    @ViewChild('file', {static: true})
    public file:any;

    @ViewChild('chatMessages', {static: true})
    public chatMessages: ElementRef | undefined;

    public deleteDialog: MatDialogRef<ConfirmDialogComponent> | undefined;

    public appUrl: string;
    private subscriptions: Subscription[] = [];

    constructor(
      private messagesQuery: MessagesQuery,
      private messagesService: MessagesService,
      private messageService: MessagesService,
      private discussionService: DiscussionService,
      private currentUser: CurrentUser,
      private snackbar: Snackbar,
      private dialog: MatDialog,
      private router: Router,
      protected conf: ConfigProvider,
      private changeDetector: ChangeDetectorRef,
      private shareService: ShareService
    ) {
        this.user = currentUser.user!;
        this.appUrl = conf.appUrl;
    }

    public ngOnInit(): void {
        // ensure store is empty before adding current discussion messages
        this.messageService.resetStore();
        this.fetchMessages('bottom');
        this.messages$ = this.messagesQuery.selectAll();
        this.messages$.subscribe(messages => {
          this.changeDetector.detectChanges();
        });
        this.isLoading$ = this.messagesQuery.selectLoading();

        this.registerMessagesUpdatedEvent();
        this.pollMessages();

        this.subscriptions.push(this.shareService.sharedEvent.subscribe(() => null));
    }

    public onFilesAdded() {
        this.files.clear();
        const files: { [key: string]: File } = this.file.nativeElement.files;
        for (const key in files) {
            if (!isNaN(parseInt(key, 10))) {
                this.files.add(files[key]);
            }
        }

        this.file.nativeElement.value = '';

        this.sendFileMessage();
    }

    public selectFileMessage(): void {
        this.file.nativeElement.click();
    }

    public sendFileMessage(): void {
        this.progress = this.messagesService.sendFileMessage(this.user.id!, this.discussion?.id!, this.discussion?.owner?.user?.id!, this.files);
        for (const key of Object.keys(this.progress)) {
            this.subscriptions.push(
                this.progress[key].observable.subscribe((res:any) => {
                    this.messagesService.add(res.file);
                }, (err: ApiError) => {
                    const data = new SnackbarData('Error_unknown');
                    data.setError(err);
                    this.snackbar.show(data);
                })
            );
        }
    }

    public sendTextMessage(messageInput: HTMLTextAreaElement): void {
        if ('' !== messageInput.value.trim()) {
            this.subscriptions.push(
                this.messagesService.sendTextMessage(this.user.id!, this.discussion?.id!, {
                    message: messageInput.value.trim()
                }).subscribe((message: TextMessage) => {
                    // update message list
                    this.messagesService.add(message);
                })
            );
        }

        messageInput.value = ''; // reset input in all cases
    }

    public deleteDiscussion(): void {
        this.deleteDialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
                confirmText: 'ChatDiscussionConfirmDelete',
                confirmActionText: 'Hide',
                color: 'accent'
            }
        });
        this.subscriptions.push(
            this.deleteDialog.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                    const data = new SnackbarData('ChatDiscussionDeleted');

                    this.subscriptions.push(
                        this.discussionService.delete(this.user, this.discussion?.id!).subscribe(() => {
                            this.discussionService.discussionDeletedEvent.emit();

                            this.closeDiscussion();
                            this.close();
                            this.snackbar.show(data);
                        }, (err: ApiError) => {
                            data.setError(err);
                            this.snackbar.show(data);
                        })
                    );
                }
            })
        );
    }

    public close(): void {
        this.closeDiscussion();
        this.viewRef?.destroy();
    }

    public onScroll() {
        // hack to prevent scroll at fetch position at initial load
        // as the infinite scroll il scrolled once during load...
        if (this.hasBeenScrolledOnce) {
            this.fetchMessages('fetch');
        } else {
            this.fetchMessages('bottom');
            this.hasBeenScrolledOnce = true;
        }
    }

    public onDownloadClicked(message:any) {
        this.subscriptions.push(
            this.messageService.download(message.authorId, message.discussionId, message.id).subscribe((response: Blob) => {
                if (response.type.includes('video')) {
                    this.dialog.open(FilesVideoPlayerDialogComponent, {
                        data: {
                            title: message.originalName,
                            src: response // TODO: pass video url

                        }
                    });
                } else if (response.type.includes('image')) {
                    this.dialog.open(FilesImageViewerDialogComponent, {
                        data: {
                            title: message.originalName,
                            data: response,
                            type: response.type,
                            downloadEnabled: false
                        }
                    });
                } else {
                    DownloaderHelper.forceDownload(response, message.originalName);
                }
            })
        );
    }

    public onLinkClicked(url:any) {
        this.router.navigateByUrl(url);
    }

    public ngOnDestroy(): void {
        this.closeDiscussion();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public getMessages() {
        this.messagesService.get(this.discussion?.owner?.user?.id!, this.discussion?.id!, 1, null, false);
    }

    public markMessagesAsSeen(): Observable<DiscussionModel> {
        return this.discussionService.markAsSeen(this.user, this.discussion!);
    }

    public closeDiscussion() {
        this.messagesService.resetStore();
    }

    private registerMessagesUpdatedEvent() {
        this.subscriptions.push(
            this.messagesService.messagesUpdatedEvent.pipe(
                debounceTime(50)
            ).subscribe((event) => {
                // scroll chat windows
                if (event === 'bottom') {
                    this.scrollAtBottom();
                } else if (event === 'fetch') {
                    this.scrollAtFetchPosition();
                }
            })
        );
    }

    private scrollAtFetchPosition() {
        if (!this.messagesQuery.getHasMore()) {
            return;
        }

        try {
            this.chatMessages!.nativeElement.scrollTop = this.chatMessages?.nativeElement.scrollHeight - this.chatMessageScrollHeight;
            // tslint:disable-next-line:no-empty
        } catch (err) {
        }
    }

    private scrollAtBottom() {
        try {
            this.chatMessages!.nativeElement.scrollTop = this.chatMessages?.nativeElement.scrollHeight;
            // tslint:disable-next-line:no-empty
        } catch (err) {
        }
    }

    private fetchMessages(fetchPosition = 'bottom') {
        if (this.messagesQuery.getHasMore()) {
            this.chatMessageScrollHeight = this.chatMessages?.nativeElement.scrollHeight;
            this.messagesService.get(this.discussion?.owner?.user?.id!, this.discussion?.id!, this.messagesQuery.getPage(), fetchPosition);
        }
    }

    /**
     * Poll for new messages every 15 seconds (default)
     *
     * @param period
     */
    private pollMessages(period = 15000) {
        this.subscriptions.push(
            interval(period).subscribe(() => {
                this.getMessages();
            })
        );
    }
}
